import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCompanies } from "@/hooks/use-companies";
import { agentsApi } from "@/api/agents";
import { queryKeys } from "@/lib/query-keys";
import { Building2, Bot, Cloud, ArrowRight } from "lucide-react";

export function AdminDashboard() {
  const navigate = useNavigate();
  const { data: companies = [] } = useCompanies();

  // Fetch agents for each company to get totals
  const agentQueries = companies.map((c) => ({
    queryKey: queryKeys.agents.byCompany(c.id),
    queryFn: () => agentsApi.listByCompany(c.id),
    enabled: companies.length > 0,
  }));

  const { data: allAgents = [] } = useQuery({
    queryKey: ["admin", "all-agents", companies.map((c) => c.id).join(",")],
    queryFn: async () => {
      const results = await Promise.all(
        companies.map((c) => agentsApi.listByCompany(c.id))
      );
      return results.flat();
    },
    enabled: companies.length > 0,
  });

  const activeAgents = allAgents.filter((a) => a.status === "active" || a.status === "running");

  const statusVariant: Record<string, "success" | "warning" | "danger" | "default"> = {
    active: "success",
    suspended: "warning",
    archived: "danger",
  };

  return (
    <CenterPanel title="Platform Dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-text-secondary">
              Total Tenants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{companies.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-text-secondary">
              Active Agents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{activeAgents.length}</p>
            <p className="text-xs text-text-muted">{allAgents.length} total</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-text-secondary">
              Total Agents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{allAgents.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tenant list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 size={18} />
            Tenants
          </CardTitle>
        </CardHeader>
        <CardContent>
          {companies.length === 0 ? (
            <p className="text-sm text-text-muted">No tenants yet.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {companies.map((company) => {
                const companyAgents = allAgents.filter((a) => a.companyId === company.id);
                return (
                  <div
                    key={company.id}
                    className="flex items-center justify-between rounded px-3 py-2.5 cursor-pointer hover:bg-surface-hover"
                    onClick={() => navigate(`/tenants/${company.id}/dashboard`)}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 size={16} className="text-accent" />
                      <div>
                        <p className="text-sm font-medium text-text-primary">{company.name}</p>
                        <p className="text-xs text-text-muted">
                          <Bot size={12} className="inline mr-1" />
                          {companyAgents.length} agent{companyAgents.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={statusVariant[company.status] ?? "default"}>
                        {company.status}
                      </Badge>
                      <ArrowRight size={16} className="text-text-muted" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </CenterPanel>
  );
}
